import type {
  CatalogEntry,
  ContentResponse,
  MatchFinishReason,
  MatchFinishedMessage,
  PublicBoard,
  PublicRoom,
} from '@bioforge/protocol';

/** Данные сетевых экранов: итог матча, гонка за боссом, знакомство с новым в лобби. */

export const FINISH_TEXT: Readonly<Record<MatchFinishReason, string>> = {
  cleared: 'Все микробы уничтожены',
  'boss-defeated': 'Босс повержен',
  overflow: 'Капсуле некуда упасть — сосуд переполнен',
  'move-limit': 'Закончились ходы',
  draw: 'Одинаковый исход у обоих',
  'player-left': 'Игрок вышел из матча',
  'disconnect-timeout': 'Игрок не вернулся после обрыва связи',
};

export type Verdict = 'won' | 'lost' | 'draw';

/**
 * Причина итога глазами игрока: в Дуэли «сосуд переполнен» или «вышел» относится
 * к проигравшему, поэтому победителю говорим о сопернике.
 */
export function finishReason(reason: MatchFinishReason, verdict: Verdict, coop: boolean): string {
  if (coop || verdict === 'draw') return FINISH_TEXT[reason];
  const won = verdict === 'won';
  switch (reason) {
    case 'overflow':
      return won ? 'Сосуд соперника переполнен' : 'Ваш сосуд переполнен — капсуле некуда упасть';
    case 'player-left':
      return won ? 'Соперник вышел из матча' : FINISH_TEXT[reason];
    case 'disconnect-timeout':
      return won ? 'Соперник не вернулся после обрыва связи' : FINISH_TEXT[reason];
    case 'cleared':
      return won ? 'Вы первым очистили банку' : 'Соперник первым очистил банку';
    case 'boss-defeated':
      return won ? 'Вы первым одолели своего босса' : 'Соперник первым одолел своего босса';
    default:
      return FINISH_TEXT[reason];
  }
}

/** Итог матча для игрока: в Co-op — итог команды, в Versus — победитель или ничья. */
export function verdictOf(finished: MatchFinishedMessage, playerId: string | null): Verdict {
  if (finished.teamWon !== null) return finished.teamWon ? 'won' : 'lost';
  if (finished.winnerId === null) return 'draw';
  return finished.winnerId === playerId ? 'won' : 'lost';
}

export function verdictTitle(verdict: Verdict, coop: boolean): string {
  if (coop) return verdict === 'won' ? 'Команда победила!' : 'Команда проиграла';
  return verdict === 'won' ? 'Победа!' : verdict === 'lost' ? 'Поражение' : 'Ничья';
}

/**
 * Насколько игрок близок к победе, 0…1: с боссом — сколько здоровья снято,
 * без босса — сколько микробов уничтожено.
 */
export function raceProgress(
  board: Pick<PublicBoard, 'boss' | 'virusCount' | 'virusesRemaining'>,
): number {
  if (board.boss) return board.boss.maxHp > 0 ? 1 - board.boss.hp / board.boss.maxHp : 0;
  if (board.virusCount <= 0) return 0;
  return Math.max(0, Math.min(1, 1 - board.virusesRemaining / board.virusCount));
}

/** Что показать в лобби впервые: босс матча, атаки и способности дуэли или роли кооператива. */
export function lobbyIntroIds(
  room: Pick<PublicRoom, 'mode' | 'bossId'>,
  loadout: readonly string[],
): string[] {
  const ids: string[] = [];
  if (room.bossId) ids.push(`boss-${room.bossId}`);
  if (room.mode === 'versus') {
    ids.push('attack-infect', ...loadout.map((kind) => `ability-${kind}`));
  } else {
    ids.push('role-pilot', 'role-strategist', 'ability-cryo', 'ability-scanner');
  }
  return [...new Set(ids)];
}

export function unseenCards(
  ids: readonly string[],
  content: Pick<ContentResponse, 'catalog'>,
  seen: { has(kind: 'intro', id: string): boolean },
): CatalogEntry[] {
  return ids
    .filter((id) => !seen.has('intro', id))
    .map((id) => content.catalog.find((entry) => entry.id === id))
    .filter((entry): entry is CatalogEntry => entry !== undefined);
}

/** «мм:сс». */
export function clock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}
